import {
    DOKUMENTE, LIEFERANT_TITEL, findeDokument, normalisiere,
    type MitgeltendesDokument,
} from '../../../services/mitgeltendeDokumente';
import type { Unterlage } from '../types';
import type { UnterlagenEntwurf } from './anlagen';

/**
 * Kapitel 9 aus dem Katalog mitgeltender Dokumente vorschlagen.
 *
 * ═══ WOZU ═════════════════════════════════════════════════════════════════
 * Der Katalog (`services/mitgeltendeDokumente`) ist die EINE Quelle fuer
 * Unterlagen wie Schadstoffgutachten, Baugrundgutachten oder
 * Entsorgungskonzept — fuer SiGe-Plan, Planungsphase und eben auch den
 * A+S-Plan. Was dort mit `asplan` markiert ist, gehoert in Kapitel 9
 * („Verwendete Unterlagen und Anlagen"); der Wert ist die vorgeschlagene Art.
 * Diese Datei sagt, welche dieser Dokumente im Plan noch fehlen, und macht
 * aus einem Vorschlag den Entwurf einer Zeile fuer `asplan_unterlagen`.
 *
 * ═══ VORSCHLAG, NICHT VORBELEGUNG ═════════════════════════════════════════
 * Uebernommen wird nur auf Knopfdruck. Eine Liste, die sich beim Oeffnen
 * selbst fuellt, saehe aus wie eine geprueft zusammengestellte Grundlage und
 * waere doch nur der Katalog (Lehre aus Handbuch und Planungsphase).
 *
 * ═══ EIN UEBERNOMMENER EINTRAG IST ROT — UND DAS IST RICHTIG ══════════════
 * Der Entwurf traegt `dokument_id: null` und `nachgereicht: false`, also den
 * Nachweisstand `nicht_abgelegt` (siehe `anlagen.ts`). Der Katalog weiss,
 * DASS ein Schadstoffgutachten gebraucht wird, nicht, DASS es vorliegt.
 * „liegt vor" waere eine unbelegte Behauptung, „wird nachgereicht" eine
 * Zusage, die niemand gemacht hat. Rot bleibt der Eintrag, bis jemand ein
 * Dokument der Projektablage verknuepft oder den Haken selbst setzt.
 *
 * ═══ NUMMER UND KENNUNG BLEIBEN LEER ══════════════════════════════════════
 * Das Modul nummeriert Unterlagen nicht: jede neue Zeile entsteht mit
 * `nr: ''` (`useAsplan.unterlageAnlegen`, `vormerkungEntwurf`,
 * `anlageAusDokument`), und Kapitel 9 druckt das Feld so, wie es steht. Eine
 * Nummer nur fuer Katalogeintraege ergaebe eine Anlagenliste, in der ein Teil
 * nummeriert ist und der Rest nicht.
 *
 * `kennung` bleibt ebenfalls leer: Kapitel 9 druckt sie bei Grundlagen in der
 * Spalte „Kennung" (dort steht die Berichtsnummer des Gutachters). Die
 * Katalog-Id dort abzulegen, braechte einen Programmschluessel aufs Blatt.
 * Wiedererkannt wird ein uebernommener Eintrag deshalb an seiner Bezeichnung.
 *
 * ═══ WIEDERERKENNUNG ══════════════════════════════════════════════════════
 * Verglichen wird normalisiert (`normalisiere`) gegen Name und Alias, und
 * zusaetzlich ueber `findeDokument`. Ausserdem zaehlt eine Bezeichnung, die
 * mit dem Namen BEGINNT und an einer Wortgrenze weitergeht: wer aus
 * „Schadstoffgutachten" ein „Schadstoffgutachten IGB vom 12.03.2025" macht,
 * soll den Vorschlag nicht zurueckbekommen. „Schadstoffgutachtenergaenzung"
 * ist dagegen ein anderes Wort und zaehlt nicht. Passen mehrere
 * Katalogeintraege, gewinnt der laengste Name — er ist der genauere.
 *
 * Diese Datei kennt weder DOM noch Supabase noch React.
 */

export type UnterlagenArt = Unterlage['art'];

/** Was die Oberflaeche zu einem Katalogdokument aufklappt. */
export interface DokumentErklaerung {
    /** WANN das Dokument benoetigt wird (Ausloeser, Schwelle). */
    wannBenoetigt: string;
    /** Was daran fuer den SiGeKo relevant ist. */
    sigekoRelevanz: string;
    /** Frist oder Vorlauf, `null` wenn der Katalog keine nennt. */
    vorlauf: string | null;
    rechtsgrundlage: string[];
    /** Alle Lieferanten im Klartext, in Katalogreihenfolge. */
    lieferanten: string[];
    /**
     * `true` = Fachpraxis, nicht im Maengelkatalog belegt. Die Oberflaeche
     * sagt das dazu: vor der Freigabe pruefen.
     */
    praxis: boolean;
}

export interface KatalogVorschlag {
    dokumentId: string;
    /** Vorgeschlagene Art in Kapitel 9 (= `MitgeltendesDokument.asplan`). */
    art: UnterlagenArt;
    /**
     * Die ROLLE des Lieferanten („Gutachter", „Bauherr"), nicht die Firma.
     * Sie ist der ehrlichste Startwert: wer die Zeile pflegt, ersetzt sie
     * durch den tatsaechlichen Herausgeber.
     */
    herausgeber: string;
    bezeichnung: string;
    erklaerung: DokumentErklaerung;
}

// ═══════════════════════════════════════════════════════════════════════════
// WIEDERERKENNUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Buchstabe oder Ziffer — dann ist an dieser Stelle keine Wortgrenze. */
const WORTZEICHEN = /[\p{L}\p{N}]/u;

/**
 * Wie gut `bezeichnung` zu `schluessel` passt: Laenge des Schluessels bei
 * Treffer, sonst 0. Beide Werte sind bereits normalisiert.
 */
const treffer = (bezeichnung: string, schluessel: string): number => {
    if (!schluessel || !bezeichnung.startsWith(schluessel)) return 0;
    const danach = bezeichnung.charAt(schluessel.length);
    if (danach && WORTZEICHEN.test(danach)) return 0;
    return schluessel.length;
};

const schluesselVon = (d: MitgeltendesDokument): string[] =>
    [d.name, ...(d.alias || [])].map(s => normalisiere(s)).filter(Boolean);

/**
 * Das Katalogdokument, dem eine Unterlage entspricht — `undefined` fuer einen
 * freien Eintrag.
 *
 * Zuerst `findeDokument` (Id, Name, Alias — die Regel des Katalogs), dann der
 * Wortgrenzen-Vergleich aus dem Kopfkommentar.
 */
export const katalogDokumentZu = (
    bezeichnung: string | null | undefined,
): MitgeltendesDokument | undefined => {
    const roh = String(bezeichnung ?? '').trim();
    if (!roh) return undefined;

    const direkt = findeDokument(roh);
    if (direkt) return direkt;

    const norm = normalisiere(roh);
    if (!norm) return undefined;

    let bester: MitgeltendesDokument | undefined;
    let besteLaenge = 0;
    for (const d of DOKUMENTE || []) {
        if (!d) continue;
        for (const s of schluesselVon(d)) {
            const laenge = treffer(norm, s);
            if (laenge > besteLaenge) {
                bester = d;
                besteLaenge = laenge;
            }
        }
    }
    return bester;
};

/** Ids der Katalogdokumente, die in den Unterlagen schon vorkommen — gleich welcher Art. */
const vorhandeneIds = (unterlagen: readonly Unterlage[]): Set<string> => {
    const ids = new Set<string>();
    for (const u of unterlagen || []) {
        if (!u) continue;
        const d = katalogDokumentZu(u.bezeichnung);
        if (d) ids.add(d.id);
    }
    return ids;
};

// ═══════════════════════════════════════════════════════════════════════════
// ERKLAERUNG UND VORSCHLAG
// ═══════════════════════════════════════════════════════════════════════════

/** Der erste Lieferant im Klartext — leer, wenn der Katalog keinen nennt. */
export const herausgeberVon = (d: Pick<MitgeltendesDokument, 'lieferant'>): string => {
    const erster = (d.lieferant || [])[0];
    return erster ? (LIEFERANT_TITEL[erster] ?? '') : '';
};

export const erklaerungVon = (d: MitgeltendesDokument): DokumentErklaerung => ({
    wannBenoetigt: String(d.wannBenoetigt || '').trim(),
    sigekoRelevanz: String(d.sigekoRelevanz || '').trim(),
    vorlauf: String(d.vorlauf || '').trim() || null,
    rechtsgrundlage: (d.rechtsgrundlage || []).map(r => String(r || '').trim()).filter(Boolean),
    lieferanten: (d.lieferant || []).map(l => LIEFERANT_TITEL[l] ?? l).filter(Boolean),
    praxis: d.beleg === 'praxis',
});

/**
 * Die Erklaerung zu einer VORHANDENEN Unterlage, wenn sie einem
 * Katalogdokument entspricht — sonst `null` (freier Eintrag).
 *
 * `name` ist der Katalogname: bei einer ergaenzten Bezeichnung („…, IGB vom
 * 12.03.2025") sieht der Anwender so, worauf sich die Erklaerung bezieht.
 */
export const katalogErklaerungZu = (
    bezeichnung: string | null | undefined,
): { name: string; erklaerung: DokumentErklaerung } | null => {
    const d = katalogDokumentZu(bezeichnung);
    return d ? { name: d.name, erklaerung: erklaerungVon(d) } : null;
};

/** Grundlagen vor Anlagen — dieselbe Reihenfolge wie im gedruckten Kapitel 9. */
const ART_REIHENFOLGE: Record<UnterlagenArt, number> = { grundlage: 0, anlage: 1 };

/**
 * Die Katalogdokumente fuer den A+S-Plan, die in Kapitel 9 noch fehlen.
 *
 * Eine vorhandene Unterlage zaehlt unabhaengig von ihrer Art: wer das
 * Gutachten als Anlage fuehrt, bekommt es nicht noch einmal als Grundlage
 * vorgeschlagen. Grundlagen stehen vor Anlagen, innerhalb einer Art gilt die
 * Reihenfolge des Katalogs.
 */
export const katalogVorschlaege = (unterlagen: readonly Unterlage[]): KatalogVorschlag[] => {
    const vorhanden = vorhandeneIds(unterlagen);
    const gesehen = new Set<string>();
    const out: KatalogVorschlag[] = [];

    for (const d of DOKUMENTE || []) {
        if (!d || !d.asplan || !d.id || !d.name) continue;
        if (vorhanden.has(d.id) || gesehen.has(d.id)) continue;
        gesehen.add(d.id);
        out.push({
            dokumentId: d.id,
            art: d.asplan,
            herausgeber: herausgeberVon(d),
            bezeichnung: d.name,
            erklaerung: erklaerungVon(d),
        });
    }

    // `sort` ist seit ES2019 stabil: die Katalogreihenfolge bleibt je Art erhalten.
    return out.sort((a, b) => ART_REIHENFOLGE[a.art] - ART_REIHENFOLGE[b.art]);
};

// ═══════════════════════════════════════════════════════════════════════════
// ENTWURF EINER NEUEN UNTERLAGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Sortierung fuer die naechste neue Zeile.
 *
 * Das Modul haengt neue Unterlagen mit `sortierung = unterlagen.length` an.
 * Nach einem Loeschen kann diese Zahl schon vergeben sein; dann gaebe es
 * zwei Zeilen mit demselben Wert, und ihre Reihenfolge haenge an der
 * Datenbank. Deshalb das Groessere aus Anzahl und hoechster Sortierung + 1 —
 * bei lueckenloser Liste dasselbe wie bisher. Hinter ALLEN Zeilen heisst
 * zugleich hinter allen Zeilen derselben Art: Kapitel 9 filtert je Art und
 * behaelt die Reihenfolge.
 */
export const naechsteSortierung = (unterlagen: readonly Unterlage[]): number => {
    const liste = (unterlagen || []).filter(Boolean);
    return liste.reduce(
        (m, u) => (Number.isFinite(u.sortierung) ? Math.max(m, u.sortierung + 1) : m),
        liste.length,
    );
};

/** Die Zeile zu einem Vorschlag, ohne die Schluessel des Buendels. */
export const entwurfAusVorschlag = (
    v: KatalogVorschlag,
    sortierung: number,
): UnterlagenEntwurf => ({
    art: v.art,
    nr: '',
    herausgeber: v.herausgeber,
    bezeichnung: v.bezeichnung,
    datum: null,
    kennung: '',
    dokument_id: null,
    nachgereicht: false,
    sortierung,
});

/**
 * Die Zeilen fuer mehrere Vorschlaege („alle uebernehmen") oder einen.
 *
 * Geprueft wird gegen den Bestand, der JETZT uebergeben wird, und nicht gegen
 * den, aus dem die Vorschlagsliste einmal entstanden ist: zwischen Anzeige
 * und Klick kann dasselbe Dokument schon angelegt worden sein (Doppelklick,
 * zweites Fenster). Bereits Vorhandenes und Doppelte fallen weg; die
 * Sortierungen schliessen lueckenlos an.
 */
export const entwuerfeAusVorschlaegen = (
    vorschlaege: readonly KatalogVorschlag[],
    unterlagen: readonly Unterlage[],
): UnterlagenEntwurf[] => {
    const vorhanden = vorhandeneIds(unterlagen);
    const start = naechsteSortierung(unterlagen);
    const out: UnterlagenEntwurf[] = [];
    for (const v of vorschlaege || []) {
        if (!v || !v.bezeichnung || vorhanden.has(v.dokumentId)) continue;
        vorhanden.add(v.dokumentId);
        out.push(entwurfAusVorschlag(v, start + out.length));
    }
    return out;
};
